import { Form, Formik, getIn } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import Confirm from '../../../components/confirm';
import ConfirmSelect from '../../../components/confirm-select';
import DisplayAddressResult from '../../../components/display-address-result';
import SaveBackdrop from '../../../components/saveBackdrop';
import Wait from '../../../components/wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  addressValidation, apiReponseError, apiRequestError, canWritePendingEnrollment,
  compareArrayObjectValue, filterCountryStates, formatDateTimeForDataGrid,
  getDefaultAddressResult, getErrorObject, getPendingEnrollmentBatchItem, isFormReferenceValid,
  isValidJSONString
} from '../../../utils/helper';
import {
  ClearAllIcon, ContactSupportIcon, DefaultSetCompanyCodeIcon, DeleteIcon, LogErrorIcon,
  SearchIcon, ShowAllIcon, ValidateAddressIcon, ValidationIcon
} from '../../../utils/icons';
import {
  Alert, AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, FormControl, FormHelperText, Grid, Hidden, IconButton, Input, InputAdornment,
  InputLabel, LinearProgress, Select, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography
} from '../../../utils/material-ui-core';

interface BatchItemKeyProps {
  batchLoadItemKey: string;
}

interface ConfirmOutput {
  status: string;
  data: any;
}

interface ReviewProps {
  open: boolean;
  keys: BatchItemKeyProps;
  onCallback: (confirmOutput: ConfirmOutput) => void;
}

const formDefaultValue = {
  accountAddressCity: "",
  accountAddressCountry: "",
  accountAddressLine1: "",
  accountAddressLine2: "",
  accountAddressState: "",
  accountAddressZip: "",
  accountName: "",
  accountNumber: "",
  batchFlowTaskName: "",
  batchLoadItemKey: "",
  bcovTiv: "",
  buildingType: "",
  companyCode: "",
  contactFirstName: "",
  contactEmail: "",
  contactLastName: "",
  contactPhone: "",
  errorMessage: "",
  groupX: "",
  inQueue: false,
  installOption: "",
  isError: false,
  kitNo: 0,
  lastModifiedBy: "",
  lastModifiedDateTime: "",
  occupancyCode: "",
  onHold: false,
  parentAccount: "",
  participantAgreementDate: "",
  participantAgreementSignedByFirstName: "",
  participantAgreementSignedByLastName: "",
  policyEffectiveDate: "",
  policyExpiryDate: "",
  policyNumber: "",
  policyType: "",
  premise: "",
  programCode: "",
  shipToAddressCity: "",
  shipToAddressCountry: "",
  shipToAddressLine1: "",
  shipToAddressLine2: "",
  shipToAddressState: "",
  shipToAddressZip: "",
  shipToEmail: "",
  shipToFirstName: "",
  shipToLastName: "",
  shipToOrganizationName: "",
  shipToPhone: "",
  signupKey: "",
  sidewalkValidationMessage: "",
  userDefinedField1: "",
  userDefinedField2: "",
  userDefinedField3: "",
  userDefinedField4: "",
  userDefinedField5: "",
  overrideAccountAddressValidation: false,
  overrideShippingAddressValidation: false,
  overrideContactPhoneValidation: false,
  overrideContactEmailValidation: false,
  overrideShippingPhoneValidation: false,
  overrideShippingEmailValidation: false,
  overrideSidewalkValidation: false,
  sidewalkValidationPassed: true,
  sidewalkValidationRequired: true,
  validCompanyCode: false,
  validProgramCode: false,
  defaultCompanyCode: "",
  advancedFlow: false,
  batchLoadItemId: 0,
  retryCount: 0,
};

const formDefaultTouchValue = {
  accountAddressCity: true,
  accountAddressCountry: true,
  accountAddressLine1: true,
  accountAddressLine2: false,
  accountAddressState: true,
  accountAddressZip: true,
  accountName: true,
  accountNumber: true,
  batchFlowTaskName: false,
  batchLoadItemKey: false,
  bcovTiv: true,
  buildingType: true,
  companyCode: true,
  contactFirstName: true,
  contactEmail: true,
  contactLastName: true,
  contactPhone: true,
  errorMessage: false,
  groupX: true,
  inQueue: false,
  installOption: true,
  isError: false,
  kitNo: true,
  lastModifiedBy: false,
  lastModifiedDateTime: false,
  occupancyCode: true,
  onHold: false,
  parentAccount: true,
  participantAgreementDate: true,
  participantAgreementSignedByFirstName: true,
  participantAgreementSignedByLastName: true,
  policyEffectiveDate: true,
  policyExpiryDate: true,
  policyNumber: true,
  policyType: true,
  premise: true,
  programCode: true,
  shipToAddressCity: true,
  shipToAddressCountry: true,
  shipToAddressLine1: true,
  shipToAddressLine2: false,
  shipToAddressState: true,
  shipToAddressZip: true,
  shipToEmail: true,
  shipToFirstName: true,
  shipToLastName: true,
  shipToOrganizationName: true,
  shipToPhone: true,
  signupKey: true,
  userDefinedField1: true,
  userDefinedField2: true,
  userDefinedField3: true,
  userDefinedField4: true,
  userDefinedField5: true,
};

const formValidationSchema = Yup.object().shape({
  companyCode: Yup.string().trim().required("Company code is required."),
  programCode: Yup.string().trim().required("Program code is required."),
  installOption: Yup.string().trim().required("Install option is required."),
  kitNo: Yup.number().required("Kit is required.").moreThan(-10, "Kit no {values.kitNo} not valid for program."),
  accountName: Yup.string().trim().required("Account name is required."),
  accountNumber: Yup.string().trim().required("Account number is required."),
  accountAddressLine1: Yup.string().trim().required("Address line 1 is required."),
  accountAddressCity: Yup.string().trim().required("City is required."),
  accountAddressState: Yup.string().trim().required("State is required."),
  accountAddressZip: Yup.string().trim().required("Postal code is required."),
  accountAddressCountry: Yup.string().trim().required("Country is required."),
  shipToOrganizationName: Yup.string().trim().required("Organization name is required."),
  shipToFirstName: Yup.string().trim().required("First name is required."),
  shipToLastName: Yup.string().trim().required("Last name is required."),
  shipToPhone: Yup.string().trim().required("Phone number is required."),
  shipToEmail: Yup.string().trim().email("Email is not valid.").required("Email is required."),
  shipToAddressLine1: Yup.string().trim().required("Address line 1 is required."),
  shipToAddressCity: Yup.string().trim().required("City is required."),
  shipToAddressState: Yup.string().trim().required("State is required."),
  shipToAddressZip: Yup.string().trim().required("Postal code is required."),
  shipToAddressCountry: Yup.string().trim().required("Country is required."),
  contactFirstName: Yup.string().trim().required("First name is required."),
  contactLastName: Yup.string().trim().required("Last name is required."),
  contactPhone: Yup.string().trim().required("Phone number is required.").max(10, "Phone number is not valid"),
  contactEmail: Yup.string().trim().email("Email is not valid.").required("Email is required."),
});

const defaultAddressResultOutput: AddressResultOutput = getDefaultAddressResult();
const initialAddressResultOutput: AddressResultOutput = getDefaultAddressResult();

const BatchItemReview = ({ open, keys, onCallback }: ReviewProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { referenceLookups, showAlert, userFunctionAccess } = useContext(AppContext);

  const formReference = useRef(null) as any;

  const [programLookupValues, setProgramLookupValues] = useState<Lookup[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [onHold, setOnHold] = useState<boolean>(false);
  const [accountAddressValidationInProgress, setAccountAddressValidationInProgress] = useState<boolean>(false);
  const [shippingAddressValidationInProgress, setShippingAddressValidationInProgress] = useState<boolean>(false);
  const [sidewalkValidationInProgress, setSidewalkValidationInProgress] = useState<boolean>(false);
  const [saveButtonVisible, setSaveButtonVisible] = useState<boolean>(false);
  const [accountAddressIsValidated, setAccountAddressIsValidated] = useState<boolean>(true);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [initialFormValues, setInitialFormValues] = useState<PendingEnrollmentBatchLoadItem>(formDefaultValue);
  const [readonly, setReadonly] = useState<boolean>(false);
  const [advancedFlow, setAdvancedFlow] = useState<boolean>(false);
  const [accountAddressResultOutput, setAccountAddressResultOutput] = useState<AddressResultOutput>(initialAddressResultOutput);
  const [shippingAddressResultOutput, setShippingAddressResultOutput] = useState<AddressResultOutput>(initialAddressResultOutput);
  const [accountStates, setAccountStates] = useState<State[]>([]);
  const [shippingStates, setShippingStates] = useState<State[]>([]);
  const [kits, setKits] = useState<LocationKit[]>([]);
  const [logs, setLogs] = useState<PendingEnrollmentBatchLoadItemLog[]>([]);
  const [programSelectConfirmOpen, setProgramSelectConfirmOpen] = useState<boolean>(false);
  const [showOnlyLogCurrentErrors, setShowOnlyLogCurrentErrors] = useState<boolean>(true);
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const [deleteConfirmDialogOpenForBatchLoadItem, setDeleteConfirmDialogOpenForBatchLoadItem] = useState<boolean>(false);
  const [isFormEdited, setIsFormEdited] = useState<boolean>(false);
  const [addressIsValidatedAndFresh, setAddressIsValidatedAndFresh] = useState<boolean>(false);

  const [initialSidewalkValidationRequired, setInitialSidewalkValidationRequired] = useState<boolean | null>(null);

  const isAccountAddressChanged = (currentValues: typeof initialFormValues) => {
    return (
      currentValues.accountAddressLine1 !== initialFormValues.accountAddressLine1 ||
      currentValues.accountAddressLine2 !== initialFormValues.accountAddressLine2 ||
      currentValues.accountAddressCity !== initialFormValues.accountAddressCity ||
      currentValues.accountAddressState !== initialFormValues.accountAddressState ||
      currentValues.accountAddressZip !== initialFormValues.accountAddressZip ||
      currentValues.accountAddressCountry !== initialFormValues.accountAddressCountry
    );
  };

  const checkSidewalkCoverage = async (explicitLat?: number, explicitLng?: number) => {
    const targetLat = explicitLat ?? accountAddressResultOutput?.latitude ?? 0;
    const targetLng = explicitLng ?? accountAddressResultOutput?.longitude ?? 0;

    const payload = { 
      latitude: Number(targetLat),
      longitude: Number(targetLng),
    };

    try {
      setSidewalkValidationInProgress(true);
      const apiResponse = await API.post("/api/validation/sidewalk", payload);
      const resData = apiResponse?.data ? apiResponse.data : apiResponse;
      
      if (resData) {
        const passed = resData.covered;
        
        formReference.current.setFieldValue("sidewalkValidationPassed", !!passed);
        formReference.current.setFieldValue("sidewalkValidationMessage", resData.message || resData.friendlyMessage || "");
        
        if (passed) {
          showAlert({ message: "Sidewalk Coverage verification passed..", severity: "success" });
          setSaveButtonVisible(true);
        } else {
          showAlert({ message: "Sidewalk Coverage verification failed.", severity: "error" });
        }
      } else {
        formReference.current.setFieldValue("sidewalkValidationPassed", false);
        showAlert({ message: "Failed to read data from sidewalk validation services.", severity: "error" });
      }
      setSidewalkValidationInProgress(false);
    } catch (e) {
      formReference.current.setFieldValue("sidewalkValidationPassed", false);
      setSidewalkValidationInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong during sidewalk validation sync."),
        severity: "error",
      });
    }
  };

  const checkAccountAddress = async () => {
    const payload = {
      address: {
        addressline1: formReference.current.values.accountAddressLine1,
        addressline2: formReference.current.values.accountAddressLine2,
        addressline3: "",
        country: formReference.current.values.accountAddressCountry,
        locality1: formReference.current.values.accountAddressCity,
        locality2: "",
        postalCode: formReference.current.values.accountAddressZip,
        region1: formReference.current.values.accountAddressState,
        region2: "",
      },
      allowPOBox: false,
      firmName: "",
    };

    const addressResultOutput = JSON.parse(JSON.stringify(accountAddressResultOutput));
    addressResultOutput.show = false;

    try {
      setAccountAddressValidationInProgress(true);
      setAccountAddressResultOutput(addressResultOutput);

      const apiResponse = await addressValidation(payload);
      if (apiResponse.success) {
        if (isValidJSONString(apiResponse.data.address)) {
          const address = apiResponse.data.address;

          if (address.valid) {
            addressResultOutput.valid = address.valid;
            addressResultOutput.message = address.friendlyMessage;
            addressResultOutput.address = address;
            addressResultOutput.addressLine = address.addressline1 + " " + address.addressline2;
            addressResultOutput.cityStatePostalCountry = address.locality1 + " " + address.region1 + " " + address.postalCode + " " + address.country;

            if (formReference.current.values.accountAddressZip.length > 5) {
              addressResultOutput.cityStatePostalCountry = address.locality1 + " " + address.region1 + " " + address.postalCodeFull + " " + address.country;
            }

            addressResultOutput.errorCodeTagged = address.latitude ? "" : address.errorCodeTagged;
            addressResultOutput.latitude = address.latitude;
            addressResultOutput.longitude = address.longitude;
            addressResultOutput.poBox = address.poBox;
            addressResultOutput.autoCorrect = address.autoCorrect;
            addressResultOutput.sameAddressLine = address.sameAddressLine;
            addressResultOutput.addressType = address.addressType;
            
            setAccountAddressResultOutput({ ...addressResultOutput });
            setAccountAddressIsValidated(true);
            setAddressIsValidatedAndFresh(true);
            setSaveButtonVisible(true); // <-- FIX: Re-enable save button on successful address validation

            setTimeout(() => {
              const isRequired = formReference.current?.values?.sidewalkValidationRequired;
              if (isRequired === true) {
                checkSidewalkCoverage(address.latitude, address.longitude);
              }
            }, 100);

          } else {
            addressResultOutput.valid = false;
            addressResultOutput.message = address.friendlyMessage;
          }
        } else {
          addressResultOutput.valid = false;
        }
      } else {
        addressResultOutput.valid = false;
        addressResultOutput.message = apiResponse.errorMessage;
      }

      addressResultOutput.show = true;
      setAccountAddressResultOutput({ ...addressResultOutput });
      setAccountAddressValidationInProgress(false);
    } catch (e) {
      addressResultOutput.valid = false;
      addressResultOutput.message = "Something was wrong in our system.";
      addressResultOutput.show = true;
      setAccountAddressResultOutput({ ...addressResultOutput });
      setAccountAddressValidationInProgress(false);
    }
  };

  const useThisAccountAddress = () => {
    setIsFormEdited(true); 
    formReference.current.setFieldValue("accountAddressLine1", accountAddressResultOutput.address.addressline1, true);
    formReference.current.setFieldValue("accountAddressLine2", accountAddressResultOutput.address.addressline2, true);
    formReference.current.setFieldValue("accountAddressCity", accountAddressResultOutput.address.locality1, true);
    formReference.current.setFieldValue("accountAddressState", accountAddressResultOutput.address.region1, true);

    if (formReference.current.values.accountAddressZip.length > 5) {
      formReference.current.setFieldValue("accountAddressZip", accountAddressResultOutput.address.postalCodeFull, true);
    } else {
      formReference.current.setFieldValue("accountAddressZip", accountAddressResultOutput.address.postalCode, true);
    }

    formReference.current.setFieldTouched("accountAddressLine1", true);
    formReference.current.setFieldTouched("accountAddressCity", true);
    formReference.current.setFieldTouched("accountAddressState", true);
    formReference.current.setFieldTouched("accountAddressZip", true);

    if (accountAddressResultOutput.address.country === "US") {
      formReference.current.setFieldValue("accountAddressCountry", "USA", true);
    } else if (accountAddressResultOutput.address.country === "CA") {
      formReference.current.setFieldValue("accountAddressCountry", "CAN", true);
    }

    formReference.current.setFieldTouched("accountAddressCountry", true);

    setSaveButtonVisible(true);
    closeAddressMessageStatus("A");

    setTimeout(() => {
      formReference.current.validateForm();
    }, 100);

    setTimeout(() => {
      if (showAddressValidationButton("A")) {
        formReference.current.setFieldValue("overrideAccountAddressValidation", true, true);
        formReference.current.setFieldTouched("overrideAccountAddressValidation", true);
        checkSidewalkCoverage(accountAddressResultOutput.latitude, accountAddressResultOutput.longitude);
      }
    }, 200);
  };

  const overrideAccountAddressValidation = () => {
    setIsFormEdited(true);
    formReference.current.setFieldValue("overrideAccountAddressValidation", false);

    if (showAddressValidationButton("A")) {
      formReference.current.setFieldValue("overrideAccountAddressValidation", true);
    }

    setSaveButtonVisible(true);
    setAccountAddressIsValidated(true);
    setAddressIsValidatedAndFresh(true);
    closeAddressMessageStatus("A");
  };

  const checkShippingAddress = async () => {
    const payload = {
      address: {
        addressline1: formReference.current.values.shipToAddressLine1,
        addressline2: formReference.current.values.shipToAddressLine2,
        addressline3: "",
        country: formReference.current.values.shipToAddressCountry,
        locality1: formReference.current.values.shipToAddressCity,
        locality2: "",
        postalCode: formReference.current.values.shipToAddressZip,
        region1: formReference.current.values.shipToAddressState,
        region2: "",
      },
      allowPOBox: false,
      firmName: "",
    };

    const addressResultOutput = JSON.parse(JSON.stringify(shippingAddressResultOutput));
    addressResultOutput.show = false;

    try {
      setShippingAddressValidationInProgress(true);
      setShippingAddressResultOutput(addressResultOutput);

      const apiResponse = await addressValidation(payload);
      if (apiResponse.success) {
        if (isValidJSONString(apiResponse.data.address)) {
          const address = apiResponse.data.address;

          if (address.valid) {
            addressResultOutput.valid = address.valid;
            addressResultOutput.message = address.friendlyMessage;
            addressResultOutput.address = address;
            addressResultOutput.addressLine = address.addressline1 + " " + address.addressline2;
            addressResultOutput.cityStatePostalCountry = address.locality1 + " " + address.region1 + " " + address.postalCode + " " + address.country;

            if (formReference.current.values.shipToAddressZip.length > 5) {
              addressResultOutput.cityStatePostalCountry = address.locality1 + " " + address.region1 + " " + address.postalCodeFull + " " + address.country;
            }

            addressResultOutput.errorCodeTagged = address.errorCodeTagged;
            addressResultOutput.latitude = address.latitude;
            addressResultOutput.longitude = address.longitude;
            addressResultOutput.poBox = address.poBox;
            addressResultOutput.autoCorrect = address.autoCorrect;
            addressResultOutput.sameAddressLine = address.sameAddressLine;
            addressResultOutput.addressType = address.addressType;
          } else {
            addressResultOutput.valid = false;
            addressResultOutput.message = address.friendlyMessage;
          }
        } else {
          addressResultOutput.valid = false;
        }
      } else {
        addressResultOutput.valid = false;
        addressResultOutput.message = apiResponse.errorMessage;
      }

      addressResultOutput.show = true;
      setShippingAddressResultOutput({ ...addressResultOutput });
      setShippingAddressValidationInProgress(false);
    } catch (e) {
      addressResultOutput.valid = false;
      addressResultOutput.message = "Something was wrong in our system.";
      addressResultOutput.show = true;
      setShippingAddressResultOutput({ ...addressResultOutput });
      setShippingAddressValidationInProgress(false);
    }
  };

  const useThisShippingAddress = () => {
    setIsFormEdited(true);
    formReference.current.setFieldValue("shipToAddressLine1", shippingAddressResultOutput.address.addressline1);
    formReference.current.setFieldValue("shipToAddressLine2", shippingAddressResultOutput.address.addressline2);
    formReference.current.setFieldValue("shipToAddressCity", shippingAddressResultOutput.address.locality1);
    formReference.current.setFieldValue("shipToAddressState", shippingAddressResultOutput.address.region1);

    if (formReference.current.values.shipToAddressZip.length > 5) {
      formReference.current.setFieldValue("shipToAddressZip", shippingAddressResultOutput.address.postalCodeFull);
    } else {
      formReference.current.setFieldValue("shipToAddressZip", shippingAddressResultOutput.address.postalCode);
    }

    formReference.current.setFieldTouched("shipToAddressLine1", true);
    formReference.current.setFieldTouched("shipToAddressCity", true);
    formReference.current.setFieldTouched("shipToAddressState", true);
    formReference.current.setFieldTouched("shipToAddressZip", true);

    if (shippingAddressResultOutput.address.country === "US") {
      formReference.current.setFieldValue("shipToAddressCountry", "USA");
    } else if (shippingAddressResultOutput.address.country === "CA") {
      formReference.current.setFieldValue("shipToAddressCountry", "CAN");
    }

    setSaveButtonVisible(true);
    closeAddressMessageStatus("S");

    setTimeout(() => {
      formReference.current.validateForm();
    }, 100);

    setTimeout(() => {
      if (showAddressValidationButton("S")) {
        formReference.current.setFieldValue("overrideShippingAddressValidation", true, true);
        formReference.current.setFieldTouched("overrideShippingAddressValidation", true);
        checkShippingAddress();
      }
    }, 200);
  };

  const overrideShippingAddressValidation = () => {
    setIsFormEdited(true);
    formReference.current.setFieldValue("overrideShippingAddressValidation", false);

    if (showAddressValidationButton("S")) {
      formReference.current.setFieldValue("overrideShippingAddressValidation", true);
    }

    setSaveButtonVisible(true);
    closeAddressMessageStatus("S");
  };

  const closeAddressMessageStatus = (type: string) => {
    if (type === "A") {
      const addressResultOutput = JSON.parse(JSON.stringify(accountAddressResultOutput));
      addressResultOutput.show = false;
      setAccountAddressResultOutput({ ...addressResultOutput });
    } else if (type === "S") {
      const addressResultOutput = JSON.parse(JSON.stringify(shippingAddressResultOutput));
      addressResultOutput.show = false;
      setShippingAddressResultOutput({ ...addressResultOutput });
    }
  };

  const resetAddressFields = (type: string) => {
    setIsFormEdited(true);
    if (type === "A") {
      formReference.current.setFieldValue("accountAddressLine1", "");
      formReference.current.setFieldValue("accountAddressLine2", "");
      formReference.current.setFieldValue("accountAddressCity", "");
      formReference.current.setFieldValue("accountAddressState", "");
      formReference.current.setFieldValue("accountAddressZip", "");
      formReference.current.setFieldTouched("accountAddressline1", true);
      formReference.current.setFieldTouched("accountAddressCity", true);
      formReference.current.setFieldTouched("accountAddressState", true);
      formReference.current.setFieldTouched("accountAddressZip", true);
      formReference.current.setFieldValue("overrideAccountAddressValidation", false);
      
      formReference.current.setFieldValue("sidewalkValidationPassed", false);
      formReference.current.setFieldValue("overrideSidewalkValidation", false);

      setTimeout(() => {
        formReference.current.validateForm();
      }, 100);
      setAccountAddressResultOutput(defaultAddressResultOutput);
      setAccountAddressIsValidated(false);
      setAddressIsValidatedAndFresh(false);
    } else if (type === "S") {
      formReference.current.setFieldValue("shipToAddressLine1", "");
      formReference.current.setFieldValue("shipToAddressLine2", "");
      formReference.current.setFieldValue("shipToAddressCity", "");
      formReference.current.setFieldValue("shipToAddressState", "");
      formReference.current.setFieldValue("shipToAddressZip", "");
      formReference.current.setFieldTouched("shipToAddressLine1", true);
      formReference.current.setFieldTouched("shipToAddressCity", true);
      formReference.current.setFieldTouched("shipToAddressState", true);
      formReference.current.setFieldTouched("shipToAddressZip", true);
      formReference.current.setFieldValue("overrideShippingAddressValidation", false);
      setTimeout(() => {
        formReference.current.validateForm();
      }, 100);
      setAccountAddressResultOutput(defaultAddressResultOutput);
    }
  };

  const showAddressValidationButton = (type: string) => {
    if (isFormReferenceValid(formReference)) {
      if (type === "A") {
        if (formReference.current.values.accountAddressline1 !== "" && formReference.current.values.accountAddressCity !== "" && formReference.current.values.accountAddressState !== "" && formReference.current.values.accountAddressZip !== "" && formReference.current.values.accountAddressCountry !== "") {
          return true;
        }
      } else if (type === "S") {
        if (formReference.current.values.shipToAddressLine1 !== "" && formReference.current.values.shipToAddressCity !== "" && formReference.current.values.shipToAddressState !== "" && formReference.current.values.shipToAddressZip !== "" && formReference.current.values.shipToAddressCountry !== "") {
          return true;
        }
      }
    }
    return false;
  };

  const handleAddressChanged = (type: string) => {
    setIsFormEdited(true);
    closeAddressMessageStatus(type);

    if (type === "A") {
      setAccountAddressIsValidated(false);
      setAddressIsValidatedAndFresh(false);
      if (showAddressValidationButton("A")) {
        formReference.current.setFieldValue("overrideAccountAddressValidation", false, true);
      }
      formReference.current.setFieldValue("sidewalkValidationPassed", false);
      formReference.current.setFieldValue("overrideSidewalkValidation", false);
    } else if (type === "S" && showAddressValidationButton("S")) {
      formReference.current.setFieldValue("overrideShippingAddressValidation", false, true);
    }
  };

  const handleCountryChanged = (event: any, type: string) => {
    setIsFormEdited(true);
    if (isFormReferenceValid(formReference)) {
      if (type === "A") {
        formReference.current.setFieldValue("accountAddressState", "");
      } else if (type === "S") {
        formReference.current.setFieldValue("shipToAddressState", "");
      }
    }
    getStates(event.target.value, type);
  };

  const getStates = (country: string, type: string) => {
    if (type === "A") {
      setAccountStates(filterCountryStates(referenceLookups.countries, country));
    } else if (type === "S") {
      setShippingStates(filterCountryStates(referenceLookups.countries, country));
    }
  };

  const setDefaultCompanyCode = async () => {
    setIsFormEdited(true);
    formReference.current.setFieldValue("companyCode", initialFormValues.defaultCompanyCode);
    formReference.current.setFieldTouched("companyCode", true);
    setSaveButtonVisible(true);
  };

  const selectedProgramSelectConfirm = (confirmOutput: ConfirmOutput) => {
    setProgramSelectConfirmOpen(false);
    if (confirmOutput.status === "select") {
      setIsFormEdited(true);
      formReference.current.setFieldValue("programCode", confirmOutput.data.value);
      formReference.current.setFieldTouched("programCode", true);
      setSaveButtonVisible(true);
    }
  };

  const resetOverrideValue = (inputName: string) => {
    try {
      if (!readonly) {
        if (inputName === "contactemail") {
          formReference.current.setFieldValue("overrideContactEmailValidation", false);
          setSaveButtonVisible(true);
        } else if (inputName === "contactphone") {
          formReference.current.setFieldValue("overrideContactPhoneValidation", false);
          setSaveButtonVisible(true);
        } else if (inputName === "shiptoemail") {
          formReference.current.setFieldValue("overrideShippingEmailValidation", false);
          setSaveButtonVisible(true);
        } else if (inputName === "shiptophone") {
          formReference.current.setFieldValue("overrideShippingPhoneValidation", false);
          setSaveButtonVisible(true);
        } else if (inputName === "sidewalkvalidation") {
          formReference.current.setFieldValue("overrideSidewalkValidation", false);
          setSaveButtonVisible(true);        
        }
      }
    } catch (e) { }
  };

  const setOverrideValue = (inputName: string) => {
    try {
      if (!readonly) {
        setIsFormEdited(true);
        if (inputName === "contactemail") {
          formReference.current.setFieldValue("overrideContactEmailValidation", !formReference.current.values.overrideContactEmailValidation);
          setSaveButtonVisible(true);
        } else if (inputName === "contactphone") {
          formReference.current.setFieldValue("overrideContactPhoneValidation", !formReference.current.values.overrideContactPhoneValidation);
          setSaveButtonVisible(true);
        } else if (inputName === "shiptoemail") {
          formReference.current.setFieldValue("overrideShippingEmailValidation", !formReference.current.values.overrideShippingEmailValidation);
          setSaveButtonVisible(true);
        } else if (inputName === "shiptophone") {
          formReference.current.setFieldValue("overrideShippingPhoneValidation", !formReference.current.values.overrideShippingPhoneValidation);
          setSaveButtonVisible(true);
        } else if (inputName === "sidewalkvalidation") {
          formReference.current.setFieldValue("overrideSidewalkValidation", !formReference.current.values.overrideSidewalkValidation);
          setSaveButtonVisible(true);
        }
      }
    } catch (e) { }
  };

  const showOverrideButton = (inputName: string) => {
    let show = false;
    try {
      let errorMessage = "";
      if (!readonly) {
        if (inputName === "contactemail") {
          if (formReference.current.values.contactEmail !== "") {
            errorMessage = formReference.current.errors.contactEmail;
            if (errorMessage === undefined) {
              errorMessage = "";
            }
            if (errorMessage.toLowerCase() !== "email is required." && errorMessage.toLowerCase() !== "email is not invalid.") {
              show = true;
            }
          }
        } else if (inputName === "sidewalkvalidation") {
          show = true;
        } 
        else if (inputName === "contactphone") {
          if (formReference.current.values.contactPhone !== "") {
            errorMessage = formReference.current.errors.contactPhone;
            if (errorMessage === undefined) {
              errorMessage = "";
            }
            if (errorMessage.toLowerCase() !== "phone number is required." && errorMessage.toLowerCase() !== "phone number is not invalid." && errorMessage.toLowerCase().indexOf("parentheses") < 0) {
              show = true;
            }
          }
        } else if (inputName === "shiptoemail") {
          if (formReference.current.values.shipToEmail !== "") {
            errorMessage = formReference.current.errors.shipToEmail;
            if (errorMessage === undefined) {
              errorMessage = "";
            }
            if (errorMessage.toLowerCase() !== "email is required." && errorMessage.toLowerCase() !== "email is not invalid.") {
              show = true;
            }
          }
        } else if (inputName === "shiptophone") {
          if (formReference.current.values.shipToPhone !== "") {
            errorMessage = formReference.current.errors.shipToPhone;
            if (errorMessage === undefined) {
              errorMessage = "";
            }
            if (errorMessage.toLowerCase() !== "phone number is required." && errorMessage.toLowerCase() !== "phone number is not invalid." && errorMessage.toLowerCase().indexOf("parentheses") < 0) {
              show = true;
            }
          }
        }
      }
    } catch (e) { }
    return show;
  };

  const getBatchILoadtemData = async () => {
    setLoading(true);
    setReadonly(true);
    setSaveButtonVisible(false);
    setSaveInProgress(true);
    setOnHold(false);

    setKits([]);
    setLogs([]);

    const apiResponse = await getPendingEnrollmentBatchItem(keys.batchLoadItemKey);
    if (apiResponse.success) {
      setInitialFormValues(apiResponse.data.pendingEnrollmentBatchLoadItem);
      setLogs(apiResponse.data.pendingEnrollmentBatchLoadItemLogs);
      setKits(apiResponse.data.kits);

      const lookupValues: Lookup[] = [];
      for (let i = 0; i < apiResponse.data.programs.length; i++) {
        lookupValues.push({
          value: apiResponse.data.programs[i].programCode,
          name: apiResponse.data.programs[i].programName + " - " + apiResponse.data.programs[i].programCode,
          checked: false,
        });
      }
      setProgramLookupValues(lookupValues.sort(compareArrayObjectValue("name", "asc")));

      getStates(apiResponse.data.pendingEnrollmentBatchLoadItem.accountAddressCountry, "A");
      getStates(apiResponse.data.pendingEnrollmentBatchLoadItem.shipToAddressCountry, "S");

      const advancedFlow = apiResponse.data.pendingEnrollmentBatchLoadItem.advancedFlow;
      const onHold = apiResponse.data.pendingEnrollmentBatchLoadItem.onHold;
      const inQueue = apiResponse.data.pendingEnrollmentBatchLoadItem.inQueue;
      const overrideAccountAddressValidation = apiResponse.data.pendingEnrollmentBatchLoadItem.overrideAccountAddressValidation;
      const overrideShippingAddressValidation = apiResponse.data.pendingEnrollmentBatchLoadItem.overrideShippingAddressValidation;

      const currentKitNo = apiResponse.data.pendingEnrollmentBatchLoadItem.kitNo;
      const currentKit = apiResponse.data.kits.find((k: LocationKit) => k.kitNo === currentKitNo);
      const initialRequired = currentKit ? ((currentKit as any).sidewalkValidationRequired ?? true) : true;

      setInitialSidewalkValidationRequired(initialRequired);

      setAdvancedFlow(advancedFlow);
      setOnHold(onHold);

      if (canWritePendingEnrollment(userFunctionAccess) && inQueue) {
        setReadonly(true);
      } else if (canWritePendingEnrollment(userFunctionAccess) && onHold) {
        setReadonly(true);
      } else {
        setReadonly(!canWritePendingEnrollment(userFunctionAccess));
      }

      setLoading(false);

      if (overrideAccountAddressValidation) {
        setAccountAddressIsValidated(true);
      } else {
        setAccountAddressIsValidated(false);
      }

      setTimeout(() => {
        if (canWritePendingEnrollment(userFunctionAccess) && !advancedFlow && !onHold && !inQueue) {
          if (!overrideAccountAddressValidation) {
            checkAccountAddress();
          }
          if (!overrideShippingAddressValidation) {
            checkShippingAddress();
          }
        }
      }, 500);

      setTimeout(() => {
        displayError();
        setSaveInProgress(false);
        setIsFormEdited(false); 
        if (formReference.current) {
          formReference.current.resetForm({ values: formReference.current.values });
        }        
      }, 2000);
    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      setSaveInProgress(false);
      cancel();
    }
  };

  const displayError = () => {
    if (readonly) {
      return false;
    }

    if (isFormReferenceValid(formReference)) {
      const errorMessage = formReference.current.values.errorMessage;

      setValidationErrorMessage("");
      if (errorMessage !== "" && errorMessage !== null && errorMessage !== undefined) {
        const errors = errorMessage.split(".");

        for (let i = 0; i < errors.length; i++) {
          if (errors[i].toLowerCase().indexOf("parent account") >= 0) {
            formReference.current.setFieldError("parentAccount", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("account number") >= 0) {
            formReference.current.setFieldError("accountNumber", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("account name") >= 0) {
            formReference.current.setFieldError("accountName", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("company code") >= 0) {
            formReference.current.setFieldError("companyCode", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("program code") >= 0) {
            formReference.current.setFieldError("programCode", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("kit no") >= 0) {
            formReference.current.setFieldError("kitNo", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("kit is inactive") >= 0) {
            formReference.current.setFieldError("kitNo", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("package") >= 0) {
            formReference.current.setFieldError("parentAccountNumber", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("signup") >= 0) {
            formReference.current.setFieldError("signupKey", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("contact") >= 0 && errors[i].toLowerCase().indexOf("phone") >= 0) {
            formReference.current.setFieldError("contactPhone", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("contact") >= 0 && errors[i].toLowerCase().indexOf("email") >= 0) {
            formReference.current.setFieldError("contactEmail", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("contact") >= 0 && errors[i].toLowerCase().indexOf("last") >= 0) {
            formReference.current.setFieldError("contactLastName", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("contact") >= 0 && errors[i].toLowerCase().indexOf("first") >= 0) {
            formReference.current.setFieldError("contactFirstName", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("ship") >= 0 && errors[i].toLowerCase().indexOf("organization") >= 0) {
            formReference.current.setFieldError("shipToOrganizationName", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("ship") >= 0 && errors[i].toLowerCase().indexOf("last") >= 0) {
            formReference.current.setFieldError("shipToLastName", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("ship") >= 0 && errors[i].toLowerCase().indexOf("first") >= 0) {
            formReference.current.setFieldError("shipToFirstName", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("ship") >= 0 && errors[i].toLowerCase().indexOf("phone") >= 0) {
            formReference.current.setFieldError("shipToPhone", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("ship") >= 0 && errors[i].toLowerCase().indexOf("email") >= 0) {
            formReference.current.setFieldError("shipToEmail", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("agreement date") >= 0) {
            formReference.current.setFieldError("participantAgreementDate", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("participant firstname") >= 0) {
            formReference.current.setFieldError("participantAgreementSignedByFirstName", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("participant last name") >= 0) {
            formReference.current.setFieldError("participantAgreementSignedByLastName", errors[i]);
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("account") >= 0 && (errors[i].toLowerCase().indexOf("address") >= 0 || errors[i].toLowerCase().indexOf("street") >= 0 || errors[i].toLowerCase().indexOf("city") >= 0 || errors[i].toLowerCase().indexOf("postal") >= 0 || errors[i].toLowerCase().indexOf("state") >= 0 || errors[i].toLowerCase().indexOf("country") >= 0)) {
            formReference.current.setFieldError("accountAddressLine1", "Invalid Address.");
            formReference.current.setFieldError("accountAddressZip", "Invalid Address.");
            formReference.current.setFieldError("accountAddressCountry", "Invalid Address.");
            formReference.current.setFieldError("accountAddressCity", "Invalid Address.");
            formReference.current.setFieldError("accountAddressState", "Invalid Address.");
            errors.splice(i, 1);
            i = -1;
          } else if (errors[i].toLowerCase().indexOf("ship") >= 0 && (errors[i].toLowerCase().indexOf("address") >= 0 || errors[i].toLowerCase().indexOf("street") >= 0 || errors[i].toLowerCase().indexOf("city") >= 0 || errors[i].toLowerCase().indexOf("postal") >= 0 || errors[i].toLowerCase().indexOf("state") >= 0 || errors[i].toLowerCase().indexOf("country") >= 0)) {
            formReference.current.setFieldError("shipToAddressLine1", "Invalid Address.");
            formReference.current.setFieldError("shipToAddressZip", "Invalid Address.");
            formReference.current.setFieldError("shipToAddressCountry", "Invalid Address.");
            formReference.current.setFieldError("shipToAddressCity", "Invalid Address.");
            formReference.current.setFieldError("shipToAddressState", "Invalid Address.");
            errors.splice(i, 1);
            i = -1;
          }
        }

        setValidationErrorMessage(errors.filter(x => x.length > 0).join(","));
        setTimeout(() => {
          scrollToFieldError(formReference);
        }, 1000);
        return errors.join(",");
      }
    }
    return "";
  };

  const scrollToFieldError = (formRef: any) => {
    if (readonly) {
      return false;
    }

    let returnValue = false;
    if (!formRef.current.isValid) {
      try {
        Object.keys(formRef.current.errors).forEach((key) => {
          let element = document.querySelector(`input[name='${key}']`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            returnValue = true;
            return;
          } else {
            element = document.querySelector(`select[name='${key}']`);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
              returnValue = true;
              return;
            }
          }
        });
      } catch (e) { }

      showAlert({
        message: "One or more validation errors occurred.",
        severity: "error",
      });
    }
    return returnValue;
  };

  const saveReProcess = async () => {
    const payload = {};
    try {
      setSaveInProgress(true);
      await API.post("/api/pendingenrollment/batch/" + keys.batchLoadItemKey + "/reprocess", payload)
        .then(async () => {
          showAlert({ message: "Successfully saved.", severity: "success" });
          await onCallback({ status: "refresh", data: "" });
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to save batch load item on reprocess."),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  const saveOnHold = async () => {
    const payload = {};
    try {
      setSaveInProgress(true);
      await API.post("/api/pendingenrollment/batch/" + keys.batchLoadItemKey + "/hold", payload)
        .then(async () => {
          showAlert({ message: "Successfully saved.", severity: "success" });
          onCallback({ status: "refresh", data: "" });
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          showAlert({
            message: apiReponseError(error, "Failed to save batch load item on hold."),
            severity: "error",
          });
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  const save = async () => {
    const payload = {
      pendingEnrollmentBatchLoadItem: {
        accountAddressCity: formReference.current.values.accountAddressCity,
        accountAddressCountry: formReference.current.values.accountAddressCountry,
        accountAddressLine1: formReference.current.values.accountAddressLine1,
        accountAddressLine2: formReference.current.values.accountAddressLine2,
        accountAddressState: formReference.current.values.accountAddressState,
        accountAddressZip: formReference.current.values.accountAddressZip,
        accountName: formReference.current.values.accountName,
        accountAddressLatitude: accountAddressResultOutput.latitude || "",
        accountAddressLongitude: accountAddressResultOutput.longitude || "",
        accountNumber: formReference.current.values.accountNumber,
        bcovTiv: formReference.current.values.bcovTiv,
        buildingType: formReference.current.values.buildingType,
        companyCode: formReference.current.values.companyCode,
        contactFirstName: formReference.current.values.contactFirstName,
        contactEmail: formReference.current.values.contactEmail,
        contactLastName: formReference.current.values.contactLastName,
        contactPhone: formReference.current.values.contactPhone,
        groupX: formReference.current.values.groupX,
        installOption: formReference.current.values.installOption,
        kitNo: formReference.current.values.kitNo,
        occupancyCode: formReference.current.values.occupancyCode,
        parentAccount: formReference.current.values.parentAccount,
        participantAgreementDate: formReference.current.values.participantAgreementDate,
        participantAgreementSignedByFirstName: formReference.current.values.participantAgreementSignedByFirstName,
        participantAgreementSignedByLastName: formReference.current.values.participantAgreementSignedByLastName,
        policyEffectiveDate: formReference.current.values.policyEffectiveDate,
        policyExpiryDate: formReference.current.values.policyExpiryDate,
        policyNumber: formReference.current.values.policyNumber,
        policyType: formReference.current.values.policyType,
        premise: formReference.current.values.premise,
        programCode: formReference.current.values.programCode,
        shipToAddressCity: formReference.current.values.shipToAddressCity,
        shipToAddressCountry: formReference.current.values.shipToAddressCountry,
        shipToAddressLine1: formReference.current.values.shipToAddressLine1,
        shipToAddressLine2: formReference.current.values.shipToAddressLine2,
        shipToAddressState: formReference.current.values.shipToAddressState,
        shipToAddressZip: formReference.current.values.shipToAddressZip,
        shipToEmail: formReference.current.values.shipToEmail,
        shipToFirstName: formReference.current.values.shipToFirstName,
        shipToLastName: formReference.current.values.shipToLastName,
        shipToOrganizationName: formReference.current.values.shipToOrganizationName,
        shipToPhone: formReference.current.values.shipToPhone,
        signupKey: formReference.current.values.signupKey,
        userDefinedField1: formReference.current.values.userDefinedField1,
        userDefinedField2: formReference.current.values.userDefinedField2,
        userDefinedField3: formReference.current.values.userDefinedField3,
        userDefinedField4: formReference.current.values.userDefinedField4,
        userDefinedField5: formReference.current.values.userDefinedField5,
        overrideAccountAddressValidation: formReference.current.values.overrideAccountAddressValidation,
        overrideShippingAddressValidation: formReference.current.values.overrideShippingAddressValidation,
        overrideContactPhoneValidation: formReference.current.values.overrideContactPhoneValidation,
        overrideContactEmailValidation: formReference.current.values.overrideContactEmailValidation,
        overrideShippingPhoneValidation: formReference.current.values.overrideShippingPhoneValidation,
        overrideShippingEmailValidation: formReference.current.values.overrideShippingEmailValidation,
        overrideSidewalkValidation: formReference.current.values.overrideSidewalkValidation,        
        sidewalkValidationPassed : formReference.current.values.sidewalkValidationPassed, 
        sidewalkValidationRequired : formReference.current.values.sidewalkValidationRequired, 
      },
    };

    try {
      setSaveInProgress(true);
      await API.post("/api/pendingenrollment/batch/" + keys.batchLoadItemKey, payload)
        .then(async () => {
          showAlert({ message: "Successfully saved.", severity: "success" });
          await onCallback({ status: "refresh", data: "" });
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "kitItem.");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save batch load item data."),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  const showDeleteConfirmationDialog = async () => {
    setDeleteConfirmDialogOpenForBatchLoadItem(true);
  };

  const deleteBatchLoadItem = async (confirm: boolean) => {
    setDeleteConfirmDialogOpenForBatchLoadItem(false);

    if (confirm) {
      try {
        setSaveInProgress(true);
        await API.delete("/api/pendingenrollment/batch/" + keys.batchLoadItemKey)
          .then(async () => {
            showAlert({
              message: "Successfully deleted.",
              severity: "success",
            });
            onCallback({ status: "refresh", data: "" });
            return;
          })
          .catch((error) => {
            setSaveInProgress(false);
            showAlert({
              message: apiReponseError(error, "Failed to delete batch load item."),
              severity: "error",
            });
          });
      } catch (e) {
        setSaveInProgress(false);
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  const cancel = () => {
    const confirmOutput: ConfirmOutput = { status: "cancel", data: "" };
    onCallback(confirmOutput);
  };

  const cleanPhoneNumber = (phone: any, type: string) => {
    let phoneNumber = phone;
    try {
      phoneNumber = String(phone).trim();
      phoneNumber = phoneNumber.replaceAll("(1", "").replaceAll("+1", "");
      phoneNumber = phoneNumber.replaceAll("(", "").replaceAll(")", "").replaceAll("-", "").replaceAll(".", "").replaceAll("+", "").replaceAll(" ", "");
    } catch { }

    if (type === "contact") {
      formReference.current.setFieldValue("contactPhone", phoneNumber);
      formReference.current.setFieldTouched("contactPhone", true, true);
    }
    else if (type === "shipping") {
      formReference.current.setFieldValue("shipToPhone", phoneNumber);
      formReference.current.setFieldTouched("shipToPhone", true, true);
    }
  };

  useEffect(() => {
    if (open) {
      if (keys.batchLoadItemKey !== "") {
        setLoading(true);
        getBatchILoadtemData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, keys]);

  return (
    <>
      <ConfirmSelect title="Select Program" label={"Program"} content={``} data={programLookupValues} yesText="SELECT" noText="CANCEL" open={programSelectConfirmOpen} multiSelect={false} maxSelect={0} callbackOnSelect={selectedProgramSelectConfirm} />

      <Confirm title="Confirmation" content={`<p>Are you sure you want to delete this account?</p><p style="text-align:center"><b>${initialFormValues.accountNumber} - ${initialFormValues.accountName}</b></p><p style="text-align:center"></p>`} yesText="DELETE" noText="CANCEL" open={deleteConfirmDialogOpenForBatchLoadItem} callbackOnClose={() => setDeleteConfirmDialogOpenForBatchLoadItem} callbackOnConfirm={deleteBatchLoadItem} />

      <Dialog className={globalStyles.dialogContainer} open={open} maxWidth={"md"} fullWidth={true}>
        <DialogTitle className={globalStyles.dialogTitleContainer}>
          <Typography variant="body1" component="div">
            {readonly ? "Enrollment View" : "Enrollment Review"}
          </Typography>
        </DialogTitle>
        {loading ? (
          <>
            <DialogContent dividers>
              <Wait />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => cancel()} color="secondary" autoFocus>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <Formik
              innerRef={formReference}
              initialValues={initialFormValues}
              enableReinitialize={true}
              validationSchema={formValidationSchema}
              validateOnMount={true}
              initialTouched={formDefaultTouchValue}
              validateOnBlur={true}
              validateOnChange={true}
              onSubmit={() => {
                save();
              }}
            >
              {({ values, errors, handleChange, setFieldValue, handleBlur, dirty }) => (
                <>
                  <DialogContent dividers>
                    <SaveBackdrop open={saveInProgress} />

                    <DialogContentText component="div">
                      <Form
                        onChange={() => { setIsFormEdited(true); }}
                      >
                        <Grid container spacing={3} className={globalStyles.formikContainer}>
                          {advancedFlow ? (
                            <Grid item xs={12}>
                              <Grid container spacing={3}>
                                <Grid item xs={12}>
                                  <Box p={1} display="flex" justifyContent="center" flexDirection="column">
                                    <Box display="flex" justifyContent="center">
                                      <b>
                                        {values.accountNumber} - {values.accountName}
                                      </b>
                                    </Box>
                                    <Box display="flex" justifyContent="center">
                                      {values.accountAddressLine1}&nbsp;
                                      {values.accountAddressLine2}
                                    </Box>
                                    <Box display="flex" justifyContent="center">
                                      {values.accountAddressCity}&nbsp;
                                      {values.accountAddressState}&nbsp;
                                      {values.accountAddressZip}&nbsp;
                                      {values.accountAddressCountry}
                                    </Box>
                                    <Box display="flex" justifyContent="center" alignContent="center" flexDirection="column">
                                      <Box p={1} display="flex" flexDirection="column" justifyContent="center">
                                        {values.retryCount > 9 ? (
                                          <>
                                            {
                                              values.errorMessage !== "" ?
                                                <>
                                                  <Box p={1}>&nbsp;</Box>
                                                  <Alert severity='error'>
                                                    {values.errorMessage}
                                                  </Alert>
                                                  <Box p={1}>&nbsp;</Box>
                                                </>
                                                : null
                                            }

                                            <Box>
                                              Tried <strong>{values.retryCount}</strong> times &amp; encountered a critical error when processing the&nbsp;`{values.batchFlowTaskName}` &nbsp;step.
                                            </Box>
                                          </>
                                        ) : (
                                          <>
                                            {
                                              values.errorMessage !== "" ?
                                                <>
                                                  <Box p={1}>&nbsp;</Box>
                                                  <Alert severity='error'>
                                                    {values.errorMessage}
                                                  </Alert>
                                                  <Box p={1}>&nbsp;</Box>
                                                </>
                                                : null
                                            }

                                            <Box>
                                              This record encountered a critical error when processing the&nbsp;`{values.batchFlowTaskName}`&nbsp;step.
                                            </Box>
                                          </>
                                        )}
                                      </Box>

                                      {values.inQueue ? (
                                        <Box p={1} display="flex" justifyContent="center">
                                          This record will automatically be reprocessed next run.{" "}
                                        </Box>
                                      ) : null}

                                      {values.onHold ? (
                                        <Box p={1} display="flex" justifyContent="center">
                                          This record is currently on hold and will not be processed until released from hold.{" "}
                                        </Box>
                                      ) : null}
                                    </Box>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Grid>
                          ) : (
                            <>
                              {!readonly && values.isError && !values.inQueue && !values.onHold ? (
                                <Grid item xs={12}>
                                  <Box p={3}>
                                    <Alert severity="error">
                                      <AlertTitle>Immediate Action Required</AlertTitle>
                                      {validationErrorMessage !== "" ? validationErrorMessage : "Validation failed. Required information is missing or not valid."}
                                    </Alert>
                                  </Box>
                                </Grid>
                              ) : null}

                              {values.isError && values.inQueue && !values.onHold ? (
                                <Grid item xs={12}>
                                  <Box p={3}>
                                    <Alert severity="info">
                                      <AlertTitle>In Queue for next run. No action required at this time.</AlertTitle>
                                      {values.errorMessage}
                                    </Alert>
                                  </Box>
                                </Grid>
                              ) : null}

                              {values.onHold ? (
                                <Grid item xs={12}>
                                  <Box p={3}>
                                    <Alert severity="info">
                                      <AlertTitle>Action Required</AlertTitle>
                                      The account is currently on hold. Click the <b>Activate</b> button to continue the process.
                                    </Alert>
                                  </Box>
                                </Grid>
                              ) : null}

                              <Grid item xs={12}>
                                <Box className={globalStyles.topic}>Policy</Box>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="policyNumber" name="policyNumber" value={values.policyNumber} label={"Policy Number"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "policyNumber"))} helperText={getIn(errors, "policyNumber")} />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="policyType" name="policyType" value={values.policyType} label={"Policy Type"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "policyType"))} helperText={getIn(errors, "policyType")} />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="policyEffectiveDate" name="policyEffectiveDate" value={values.policyEffectiveDate} label={"Policy Effective Date"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "policyEffectiveDate"))} helperText={getIn(errors, "policyEffectiveDate")} />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="policyExpiryDate" name="policyExpiryDate" value={values.policyExpiryDate} label={"Policy Expiration Date"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "policyExpiryDate"))} helperText={getIn(errors, "policyExpiryDate")} />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="buildingType" name="buildingType" value={values.buildingType} label={"Building Type"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "buildingType"))} helperText={getIn(errors, "buildingType")} />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="buildingvType" name="occupancyCode	" value={values.occupancyCode} label={"Occupancy Code"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "occupancyCode	"))} helperText={getIn(errors, "occupancyCode	")} />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="premise" name="premise" value={values.premise} label={"Premise"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "premise"))} helperText={getIn(errors, "premise")} />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="groupX" name="groupX" value={values.groupX} label={"Group"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "groupX"))} helperText={getIn(errors, "groupX")} />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="bcovTiv" name="bcovTiv" value={values.bcovTiv} label={"BCov TIV"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "bcovTiv"))} helperText={getIn(errors, "bcovTiv")} />
                              </Grid>

                              <Grid item xs={12}>
                                <Box p={1}></Box>
                              </Grid>

                              <Grid item xs={12}>
                                <Box className={globalStyles.topic}>Account</Box>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="accountNumber" name="accountNumber" value={values.accountNumber} label={"Account Number"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "accountNumber"))} helperText={getIn(errors, "accountNumber")} />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="accountName" name="accountName" value={values.accountName} label={"Account Name"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "accountName"))} helperText={getIn(errors, "accountName")} />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth={true}>
                                  <InputLabel>Company Code</InputLabel>
                                  <Input
                                    disabled={true}
                                    id="companyCode"
                                    name="companyCode"
                                    value={values.companyCode}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={Boolean(getIn(errors, "companyCode"))}
                                    endAdornment={
                                      <InputAdornment position="end">
                                        {!readonly && !values.validCompanyCode ? (
                                          <React.Fragment>
                                            <Tooltip arrow title="Set Default Company Code">
                                              <IconButton onClick={() => setDefaultCompanyCode()}>
                                                <DefaultSetCompanyCodeIcon />
                                              </IconButton>
                                            </Tooltip>
                                          </React.Fragment>
                                        ) : (
                                          <></>
                                        )}
                                      </InputAdornment>
                                    }
                                  />
                                  <FormHelperText error>{getIn(errors, "companyCode")}</FormHelperText>
                                </FormControl>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth={true}>
                                  <InputLabel>Program</InputLabel>
                                  <Input
                                    disabled={true}
                                    id="programCode"
                                    name="programCode"
                                    value={values.programCode}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={Boolean(getIn(errors, "programCode"))}
                                    endAdornment={
                                      <InputAdornment position="end">
                                        {!readonly && values.validCompanyCode && !values.validProgramCode ? (
                                          <React.Fragment>
                                            <Tooltip arrow title="Select Program">
                                              <IconButton onClick={() => setProgramSelectConfirmOpen(true)}>
                                                <SearchIcon />
                                              </IconButton>
                                            </Tooltip>
                                          </React.Fragment>
                                        ) : (
                                          <></>
                                        )}
                                      </InputAdornment>
                                    }
                                  />
                                  <FormHelperText error>{getIn(errors, "programCode")}</FormHelperText>
                                </FormControl>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="parentAccount" name="parentAccount" value={values.parentAccount} label={"Parent Account"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "parentAccount"))} helperText={getIn(errors, "parentAccount")} />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="signupKey" name="signupKey" value={values.signupKey} label={"Signup Key"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "signupKey"))} helperText={getIn(errors, "signupKey")} />
                              </Grid>

                              <Grid item xs={6} sm={6}>
                                <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "kitNo"))}>
                                  <InputLabel id="state-label" shrink={true}>
                                    Kit
                                  </InputLabel>
                                  <Select 
                                    disabled={readonly} 
                                    native 
                                    labelId="kit-no-label" 
                                    id="kitNo" 
                                    name="kitNo" 
                                    value={values.kitNo} 
                                    onChange={(e) => {
                                      const selectedKitNo = Number(e.target.value);
                                      
                                      // 1. Explicitly update Formik value instead of handleChange
                                      setFieldValue("kitNo", selectedKitNo);

                                      const selectedKit = kits.find(k => k.kitNo === selectedKitNo);

                                      // 2. Clear override using Formik state only
                                      setFieldValue("overrideSidewalkValidation", false);

                                      let isRequired = true;

                                      if (selectedKit) {                                        
                                        isRequired = (selectedKit as any).sidewalkValidationRequired ?? true;
                                        setFieldValue("sidewalkValidationRequired", isRequired);

                                        if (!isRequired) {                                          
                                          setFieldValue("sidewalkValidationMessage", `Sidewalk validation is not required for kit ${selectedKitNo}.`);
                                        } else {
                                          setFieldValue("sidewalkValidationMessage", "");

                                          if (initialSidewalkValidationRequired === false && values.sidewalkValidationPassed !== false) {
                                            checkSidewalkCoverage();
                                          }
                                        }
                                      } else {
                                        isRequired = true;
                                        setFieldValue("sidewalkValidationRequired", true);
                                        setFieldValue("sidewalkValidationMessage", "");

                                        if (initialSidewalkValidationRequired === false && values.sidewalkValidationPassed !== false) {
                                          checkSidewalkCoverage();
                                        }
                                      }

                                      const canSave = values.sidewalkValidationPassed === true || isRequired === false;
                                      setSaveButtonVisible(canSave);
                                    }}
                                    label="Kit" 
                                    error={Boolean(getIn(errors, "kitNo"))}
                                  >
                                    <option value={0}></option>
                                    {kits.map((location, index) => (
                                      <option value={location.kitNo} key={index}>
                                        {location.kitNo} - {location.name}
                                      </option>
                                    ))}
                                  </Select>
                                  <FormHelperText error>{getIn(errors, "kitNo")}</FormHelperText>
                                </FormControl>
                              </Grid>

                              <Grid item xs={6} sm={6}>
                                <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "kitNo"))}>
                                  <InputLabel id="state-label" shrink={true}>
                                    Install Method
                                  </InputLabel>
                                  <Select disabled={readonly} native labelId="install-method-id-label" id="installOption" name="installOption" value={values.installOption} onChange={handleChange} label="Install Method" error={Boolean(getIn(errors, "installOption"))}>
                                    <option value={""}></option>
                                    <option value={"pro"}>Pro</option>
                                    <option value={"self"}>Self</option>
                                    <option value={"vgi"}>VGI</option>
                                  </Select>
                                  <FormHelperText error>{getIn(errors, "installOption")}</FormHelperText>
                                </FormControl>
                              </Grid>

                              <Grid item xs={12}>
                                <Box className={globalStyles.topic} mb={2}>
                                  <Grid container>
                                    <Grid item xs={8}>
                                      Sidewalk Validation
                                    </Grid>
                                  </Grid>  
                                </Box>

                                {typeof window !== "undefined" && 
                                (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") && (
                                  <div className="space-y-2 font-mono text-sm">
                                    <div>
                                      <strong>sidewalkValidationRequired:</strong> {String(values.sidewalkValidationRequired)}
                                    </div>
                                    <div>
                                      <strong>overrideSidewalkValidation:</strong> {String(values.overrideSidewalkValidation)}
                                    </div>
                                    <div>
                                      <strong>sidewalkValidationPassed:</strong> {String(values.sidewalkValidationPassed)}
                                    </div>
                                  </div>
                                )}                                

                                {sidewalkValidationInProgress && !saveInProgress ? (
                                  <Alert severity="info">
                                    Sidewalk validation is currently processing...
                                  </Alert>
                                ) : isAccountAddressChanged(values) && !addressIsValidatedAndFresh && !values.sidewalkValidationPassed ? (
                                  <Alert severity="warning">
                                    Please Validate the account address.
                                  </Alert>
                                  
                                ) : !values.sidewalkValidationRequired ? (
                                  <Alert severity="success">
                                    Sidewalk validation is not required for kit {values.kitNo}.
                                  </Alert>
                                  
                                ) : values.overrideSidewalkValidation ? (
                                  <Alert 
                                    severity="warning"
                                    action={
                                      !readonly ? (
                                        <Button color="inherit" size="small" onClick={() => {
                                          setFieldValue("overrideSidewalkValidation", false);
                                          setIsFormEdited(true); 
                                          setSaveButtonVisible(true);
                                        }}>
                                          Undo Override
                                        </Button>
                                      ) : null
                                    }
                                  >
                                    Sidewalk Validation Overridden
                                  </Alert>

                                ) : values.sidewalkValidationPassed ? (
                                  <Alert severity="success">
                                    {values.sidewalkValidationMessage || "Sidewalk validation passed."}
                                  </Alert>

                                ) : (
                                  <Alert severity="error">
                                    {values.sidewalkValidationMessage || "Sidewalk validation failed."}
                                    <br /><br />
                                    <Button 
                                      className={globalStyles.buttonDefault} 
                                      variant="contained" 
                                      size="small" 
                                      onClick={() => {
                                        setIsFormEdited(true); // Mark form as dirty/edited
                                        setFieldValue("overrideSidewalkValidation", true);
                                        setSaveButtonVisible(true);
                                      }}
                                    >
                                      Override Validation
                                    </Button>
                                  </Alert>
                                )}
                              </Grid>      

                              {/* Account Address */}
                              <Grid item xs={12}>
                                <Box className={globalStyles.topic}>
                                  {accountAddressValidationInProgress ? <LinearProgress color="secondary" /> : null}
                                  <Grid container>
                                    <Grid item xs={8}>
                                      Account Address
                                    </Grid>
                                    <Grid item xs={4}>
                                      <Box display="flex" justifyContent="flex-end" width={1}>
                                        {!readonly ? (
                                          <React.Fragment>
                                            {!accountAddressValidationInProgress ? (
                                              <>
                                                {showAddressValidationButton("A") ? (
                                                  <Tooltip arrow title="Validate address">
                                                    <IconButton onClick={() => checkAccountAddress()}>
                                                      <ValidateAddressIcon color="secondary" />
                                                    </IconButton>
                                                  </Tooltip>
                                                ) : null}
                                              </>
                                            ) : null}
                                            <Typography variant="body1">&nbsp;&nbsp;&nbsp;</Typography>
                                            <Tooltip arrow title="Clear address">
                                              <IconButton onClick={() => resetAddressFields("A")}>
                                                <ClearAllIcon color="error" />
                                              </IconButton>
                                            </Tooltip>
                                          </React.Fragment>
                                        ) : null}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Grid>

                              {accountAddressResultOutput.show ? (
                                <Grid item xs={12}>
                                  <DisplayAddressResult
                                    addressResultOutput={accountAddressResultOutput}
                                    currentAddress={{
                                      addressline1: values.accountAddressLine1,
                                      addressline2: values.accountAddressLine2,
                                      addressline3: "",
                                      country: values.accountAddressCountry,
                                      locality1: values.accountAddressCity,
                                      locality2: "",
                                      postalCode: values.accountAddressZip,
                                      region1: values.accountAddressState,
                                      region2: "",
                                    }}
                                    callbackOnClose={() => {
                                      closeAddressMessageStatus("A");
                                    }}
                                    useThisAddressButton={true}
                                    callbackOnUseThisAddress={useThisAccountAddress}
                                    overrideValidationAddress={true}
                                    callbackOnOverrideValidationAddress={overrideAccountAddressValidation}
                                  />
                                </Grid>
                              ) : null}
                              {values.overrideAccountAddressValidation ? (
                                <Grid item xs={12}>
                                  <Alert severity="info">Account address validation is overwritten.</Alert>
                                </Grid>
                              ) : null}
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  disabled={readonly}
                                  id="accountAddressLine1"
                                  name="accountAddressLine1"
                                  label="Address Line 1"
                                  value={values.accountAddressLine1}
                                  onChange={(e) => {
                                    handleChange(e);
                                    handleAddressChanged("A");
                                  }}
                                  onBlur={handleBlur}
                                  error={Boolean(getIn(errors, "accountAddressLine1"))}
                                  helperText={getIn(errors, "accountAddressLine1")}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  disabled={readonly}
                                  id="accountAddressLine2"
                                  name="accountAddressLine2"
                                  label="Address Line 2"
                                  value={values.accountAddressLine2}
                                  onChange={(e) => {
                                    handleChange(e);
                                    handleAddressChanged("A");
                                  }}
                                  onBlur={handleBlur}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  disabled={readonly}
                                  id="accountAddressCity"
                                  name="accountAddressCity"
                                  label="City"
                                  value={values.accountAddressCity}
                                  onChange={(e) => {
                                    handleChange(e);
                                    handleAddressChanged("A");
                                  }}
                                  onBlur={handleBlur}
                                  error={Boolean(getIn(errors, "accountAddressCity"))}
                                  helperText={getIn(errors, "accountAddressCity")}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "accountAddressState"))}>
                                  <InputLabel id="state-label" shrink={true}>
                                    State
                                  </InputLabel>
                                  <Select
                                    disabled={readonly}
                                    native
                                    labelId="state-label"
                                    id="accountAddressState"
                                    name="accountAddressState"
                                    value={values.accountAddressState}
                                    onChange={(e) => {
                                      handleChange(e);
                                      handleAddressChanged("A");
                                    }}
                                    label="State"
                                    error={Boolean(getIn(errors, "accountAddressState"))}
                                  >
                                    <option value={""}></option>
                                    {accountStates.map((state, index) => (
                                      <option value={state.code} key={index}>
                                        {state.name}
                                      </option>
                                    ))}
                                  </Select>
                                  <FormHelperText error>{getIn(errors, "accountAddressState")}</FormHelperText>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  disabled={readonly}
                                  id="accountAddressZip"
                                  name="accountAddressZip"
                                  label="Postal Code"
                                  value={values.accountAddressZip}
                                  onChange={(e) => {
                                    handleChange(e);
                                    handleAddressChanged("A");
                                  }}
                                  onBlur={handleBlur}
                                  error={Boolean(getIn(errors, "accountAddressZip"))}
                                  helperText={getIn(errors, "accountAddressZip")}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "accountAddressCountry"))}>
                                  <InputLabel id="country-label" shrink={true}>
                                    Country
                                  </InputLabel>
                                  <Select
                                    disabled={readonly}
                                    native
                                    labelId="country-label"
                                    id="accountAddressCountry"
                                    name="accountAddressCountry"
                                    value={values.accountAddressCountry}
                                    onChange={(e) => {
                                      handleAddressChanged("A");
                                      handleChange(e);
                                      handleCountryChanged(e, "A");
                                    }}
                                    label="Country"
                                    error={Boolean(getIn(errors, "accountAddressCountry"))}
                                  >
                                    <option value={""}></option>
                                    {referenceLookups.countries.map((country, index) => (
                                      <option value={country.code} key={index}>
                                        {country.name}
                                      </option>
                                    ))}
                                  </Select>
                                  <FormHelperText error>{getIn(errors, "accountAddressCountry")}</FormHelperText>
                                </FormControl>
                              </Grid>
                              
                              {/* Read-Only Coordinate Metrics Mapping */}
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  disabled={true}
                                  id="latitude"
                                  label="Latitude"
                                  value={accountAddressResultOutput.latitude || ""}
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  disabled={true}
                                  id="longitude"
                                  label="Longitude"
                                  value={accountAddressResultOutput.longitude || ""}
                                  fullWidth
                                />
                              </Grid>

                              {/* Contact */}
                              <Grid item xs={12}>
                                <Box p={1}></Box>
                              </Grid>

                              <Grid item xs={12}>
                                <Box className={globalStyles.topic}>Contact</Box>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="contactFirstName" name="contactFirstName" value={values.contactFirstName} label={"First Name"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "contactFirstName"))} helperText={getIn(errors, "contactFirstName")} />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="contactLastName" name="contactLastName" value={values.contactLastName} label={"Last Name"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "contactLastName"))} helperText={getIn(errors, "contactLastName")} />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth={true}>
                                  <InputLabel>Phone</InputLabel>
                                  <Input
                                    disabled={readonly}
                                    id="contactPhone"
                                    name="contactPhone"
                                    value={values.contactPhone}
                                    onChange={(e) => {
                                      handleChange(e);
                                      resetOverrideValue("contactphone");
                                      setTimeout(() => {
                                        cleanPhoneNumber(e.target.value, "contact");
                                      }, 1000);
                                    }}
                                    onBlur={handleBlur}
                                    error={Boolean(getIn(errors, "contactPhone"))}
                                    endAdornment={
                                      <InputAdornment position="end">
                                        {!readonly && showOverrideButton("contactphone") ? (
                                          <React.Fragment>
                                            <Tooltip arrow title="Override Validation">
                                              <IconButton onClick={() => setOverrideValue("contactphone")}>{values.overrideContactPhoneValidation ? <ValidationIcon color="primary" /> : <ValidationIcon />}</IconButton>
                                            </Tooltip>
                                          </React.Fragment>
                                        ) : (
                                          <></>
                                        )}
                                      </InputAdornment>
                                    }
                                  />
                                  <FormHelperText error>{getIn(errors, "contactPhone")}</FormHelperText>
                                </FormControl>
                                {values.overrideContactPhoneValidation ? <Alert severity="info">Contact phone validation is overwritten.</Alert> : null}
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth={true}>
                                  <InputLabel>Email</InputLabel>
                                  <Input
                                    disabled={readonly}
                                    id="contactEmail"
                                    name="contactEmail"
                                    value={values.contactEmail}
                                    onChange={(e) => {
                                      handleChange(e);
                                      resetOverrideValue("contactemail");
                                    }}
                                    onBlur={handleBlur}
                                    error={Boolean(getIn(errors, "contactEmail"))}
                                    endAdornment={
                                      <InputAdornment position="end">
                                        {!readonly && showOverrideButton("contactemail") ? (
                                          <React.Fragment>
                                            <Tooltip arrow title="Override Validation">
                                              <IconButton onClick={() => setOverrideValue("contactemail")}>{values.overrideContactEmailValidation ? <ValidationIcon color="primary" /> : <ValidationIcon />}</IconButton>
                                            </Tooltip>
                                          </React.Fragment>
                                        ) : (
                                          <></>
                                        )}
                                      </InputAdornment>
                                    }
                                  />
                                  <FormHelperText error>{getIn(errors, "contactEmail")}</FormHelperText>
                                </FormControl>
                                {values.overrideContactEmailValidation ? <Alert severity="info">Contact email validation is overwritten.</Alert> : null}
                              </Grid>

                              {/* Shipping*/}
                              <Grid item xs={12}>
                                <Box p={1}></Box>
                              </Grid>

                              <Grid item xs={12}>
                                <Box className={globalStyles.topic}>
                                  {shippingAddressValidationInProgress ? <LinearProgress color="secondary" /> : null}
                                  <Grid container>
                                    <Grid item xs={8}>
                                      Shipping Address
                                    </Grid>
                                    <Grid item xs={4}>
                                      <Box display="flex" justifyContent="flex-end" width={1}>
                                        {!readonly ? (
                                          <React.Fragment>
                                            {!shippingAddressValidationInProgress ? (
                                              <>
                                                {showAddressValidationButton("S") ? (
                                                  <Tooltip arrow title="Validate address">
                                                    <IconButton onClick={() => checkShippingAddress()}>
                                                      <ValidateAddressIcon color="secondary" />
                                                    </IconButton>
                                                  </Tooltip>
                                                ) : null}
                                              </>
                                            ) : null}
                                            <Typography variant="body1">&nbsp;&nbsp;&nbsp;</Typography>
                                            <Tooltip arrow title="Clear address">
                                              <IconButton onClick={() => resetAddressFields("S")}>
                                                <ClearAllIcon color="error" />
                                              </IconButton>
                                            </Tooltip>
                                          </React.Fragment>
                                        ) : null}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Grid>

                              {shippingAddressResultOutput.show ? (
                                <Grid item xs={12}>
                                  <DisplayAddressResult
                                    addressResultOutput={shippingAddressResultOutput}
                                    currentAddress={{
                                      addressline1: values.shipToAddressLine1,
                                      addressline2: values.shipToAddressLine2,
                                      addressline3: "",
                                      country: values.shipToAddressCountry,
                                      locality1: values.shipToAddressCity,
                                      locality2: "",
                                      postalCode: values.shipToAddressZip,
                                      region1: values.shipToAddressState,
                                      region2: "",
                                    }}
                                    callbackOnClose={() => {
                                      closeAddressMessageStatus("S");
                                    }}
                                    useThisAddressButton={true}
                                    callbackOnUseThisAddress={useThisShippingAddress}
                                    overrideValidationAddress={true}
                                    callbackOnOverrideValidationAddress={overrideShippingAddressValidation}
                                  />
                                </Grid>
                              ) : null}

                              {values.overrideShippingAddressValidation ? (
                                <Grid item xs={12}>
                                  <Alert severity="info">Shipping address validation is overwritten.</Alert>
                                </Grid>
                              ) : null}

                              <Grid item xs={12}>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} sm={6}>
                                    <TextField disabled={readonly} id="shipToOrganizationName" name="shipToOrganizationName" value={values.shipToOrganizationName} label={"Organization Name"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "shipToOrganizationName"))} helperText={getIn(errors, "shipToOrganizationName")} />
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  disabled={readonly}
                                  id="addressline1"
                                  name="shipToAddressLine1"
                                  label="Address Line 1"
                                  value={values.shipToAddressLine1}
                                  onChange={(e) => {
                                    handleChange(e);
                                    handleAddressChanged("S");
                                  }}
                                  onBlur={handleBlur}
                                  error={Boolean(getIn(errors, "shipToAddressLine1"))}
                                  helperText={getIn(errors, "shipToAddressLine1")}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  disabled={readonly}
                                  id="shipToAddressLine2"
                                  name="shipToAddressLine2"
                                  label="Address Line 2"
                                  value={values.shipToAddressLine2}
                                  onChange={(e) => {
                                    handleChange(e);
                                    handleAddressChanged("S");
                                  }}
                                  onBlur={handleBlur}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  disabled={readonly}
                                  id="shipToAddressCity"
                                  name="shipToAddressCity"
                                  label="City"
                                  value={values.shipToAddressCity}
                                  onChange={(e) => {
                                    handleChange(e);
                                    handleAddressChanged("S");
                                  }}
                                  onBlur={handleBlur}
                                  error={Boolean(getIn(errors, "shipToAddressCity"))}
                                  helperText={getIn(errors, "shipToAddressCity")}
                                />
                              </Grid>
                              <Grid item xs={6} sm={6}>
                                <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "shipToAddressState"))}>
                                  <InputLabel id="state-label" shrink={true}>
                                    State
                                  </InputLabel>
                                  <Select
                                    disabled={readonly}
                                    native
                                    labelId="state-label"
                                    id="shipToAddressState"
                                    name="shipToAddressState"
                                    value={values.shipToAddressState}
                                    onChange={(e) => {
                                      handleChange(e);
                                      handleAddressChanged("S");
                                    }}
                                    label="State"
                                    error={Boolean(getIn(errors, "shipToAddressState"))}
                                  >
                                    <option value={""}></option>
                                    {shippingStates.map((state, index) => (
                                      <option value={state.code} key={index}>
                                        {state.name}
                                      </option>
                                    ))}
                                  </Select>
                                  <FormHelperText error>{getIn(errors, "shipToAddressState")}</FormHelperText>
                                </FormControl>
                              </Grid>
                              <Grid item xs={6} sm={6}>
                                <TextField
                                  disabled={readonly}
                                  id="shipToAddressZip"
                                  name="shipToAddressZip"
                                  label="Postal Code"
                                  value={values.shipToAddressZip}
                                  onChange={(e) => {
                                    handleChange(e);
                                    handleAddressChanged("S");
                                  }}
                                  onBlur={handleBlur}
                                  error={Boolean(getIn(errors, "shipToAddressZip"))}
                                  helperText={getIn(errors, "shipToAddressZip")}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "shipToAddressCountry"))}>
                                  <InputLabel id="country-label" shrink={true}>
                                    Country
                                  </InputLabel>
                                  <Select
                                    disabled={readonly}
                                    native
                                    labelId="country-label"
                                    id="shipToAddressCountry"
                                    name="shipToAddressCountry"
                                    value={values.shipToAddressCountry}
                                    onChange={(e) => {
                                      handleAddressChanged("S");
                                      handleChange(e);
                                      handleCountryChanged(e, "S");
                                    }}
                                    label="Country"
                                    error={Boolean(getIn(errors, "shipToAddressCountry"))}
                                  >
                                    <option value={""}></option>
                                    {referenceLookups.countries.map((country, index) => (
                                      <option value={country.code} key={index}>
                                        {country.name}
                                      </option>
                                    ))}
                                  </Select>
                                  <FormHelperText error>{getIn(errors, "shipToAddressCountry")}</FormHelperText>
                                </FormControl>
                              </Grid>

                              {/* Shipping Contact */}
                              <Grid item xs={12}></Grid>

                              <Grid item xs={12}>
                                <Box className={globalStyles.topic}>Shipping Contact</Box>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="shipToFirstName" name="shipToFirstName" value={values.shipToFirstName} label={"First Name"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "shipToFirstName"))} helperText={getIn(errors, "shipToFirstName")} />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="shipToLastName" name="shipToLastName" value={values.shipToLastName} label={"Last Name"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "shipToLastName"))} helperText={getIn(errors, "shipToLastName")} />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth={true}>
                                  <InputLabel>Phone</InputLabel>
                                  <Input
                                    disabled={readonly}
                                    id="shipToPhone"
                                    name="shipToPhone"
                                    value={values.shipToPhone}
                                    onChange={(e) => {
                                      handleChange(e);
                                      resetOverrideValue("shiptophone");
                                      setTimeout(() => {
                                        cleanPhoneNumber(e.target.value, "shipping");
                                      }, 1000);
                                    }}
                                    onBlur={handleBlur}
                                    error={Boolean(getIn(errors, "shipToPhone"))}
                                    endAdornment={
                                      <InputAdornment position="end">
                                        {!readonly && showOverrideButton("shiptophone") ? (
                                          <React.Fragment>
                                            <Tooltip arrow title="Override Validation">
                                              <IconButton onClick={() => setOverrideValue("shiptophone")}>{values.overrideShippingPhoneValidation ? <ValidationIcon color="primary" /> : <ValidationIcon />}</IconButton>
                                            </Tooltip>
                                          </React.Fragment>
                                        ) : (
                                          <></>
                                        )}
                                      </InputAdornment>
                                    }
                                  />
                                  <FormHelperText error>{getIn(errors, "shipToPhone")}</FormHelperText>
                                </FormControl>
                                {values.overrideShippingPhoneValidation ? <Alert severity="info">Ship to phone validation is overwritten.</Alert> : null}
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth={true}>
                                  <InputLabel>Email</InputLabel>
                                  <Input
                                    disabled={readonly}
                                    id="shipToEmail"
                                    name="shipToEmail"
                                    value={values.shipToEmail}
                                    onChange={(e) => {
                                      handleChange(e);
                                      resetOverrideValue("shipToEmail");
                                    }}
                                    onBlur={handleBlur}
                                    error={Boolean(getIn(errors, "shipToEmail"))}
                                    endAdornment={
                                      <InputAdornment position="end">
                                        {!readonly && showOverrideButton("shiptoemail") ? (
                                          <React.Fragment>
                                            <Tooltip arrow title="Override Validation">
                                              <IconButton onClick={() => setOverrideValue("shiptoemail")}>{values.overrideShippingEmailValidation ? <ValidationIcon color="primary" /> : <ValidationIcon />}</IconButton>
                                            </Tooltip>
                                          </React.Fragment>
                                        ) : (
                                          <></>
                                        )}
                                      </InputAdornment>
                                    }
                                  />
                                  <FormHelperText error>{getIn(errors, "shipToEmail")}</FormHelperText>
                                </FormControl>
                                {values.overrideShippingEmailValidation ? <Alert severity="info">Ship to email validation is overwritten.</Alert> : null}
                              </Grid>

                              {/* Participant Agreement */}
                              <Grid item xs={12}>
                                <Box p={1}></Box>
                              </Grid>

                              <Grid item xs={12}>
                                <Box className={globalStyles.topic}>Participant Agreement</Box>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="participantAgreementSignedByFirstName" name="participantAgreementSignedByFirstName" value={values.participantAgreementSignedByFirstName} label={"First Name"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "participantAgreementSignedByFirstName"))} helperText={getIn(errors, "participantAgreementSignedByFirstName")} />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="participantAgreementSignedByLastName" name="participantAgreementSignedByLastName" value={values.participantAgreementSignedByLastName} label={"Last Name"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "participantAgreementSignedByLastName"))} helperText={getIn(errors, "participantAgreementSignedByLastName")} />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} type="date" id="participantAgreementDate" name="participantAgreementDate" value={values.participantAgreementDate} label={"Date"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "participantAgreementDate"))} helperText={getIn(errors, "participantAgreementDate")} />
                              </Grid>

                              {/* User Define Values */}
                              <Grid item xs={12}>
                                <Box p={1}></Box>
                              </Grid>

                              <Grid item xs={12}>
                                <Box className={globalStyles.topic}>User Defined Values</Box>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="userDefinedField1" name="userDefinedField1" value={values.userDefinedField1} label={"Field 1"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "userDefinedField1"))} helperText={getIn(errors, "userDefinedField1")} />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="userDefinedField2" name="userDefinedField2" value={values.userDefinedField2} label={"Field 2"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "userDefinedField2"))} helperText={getIn(errors, "userDefinedField2")} />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="userDefinedField3" name="userDefinedField3" value={values.userDefinedField3} label={"Field 3"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "userDefinedField3"))} helperText={getIn(errors, "userDefinedField3")} />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="userDefinedField4" name="userDefinedField4" value={values.userDefinedField4} label={"Field 4"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "userDefinedField4"))} helperText={getIn(errors, "userDefinedField4")} />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField disabled={readonly} id="userDefinedField5" name="userDefinedField5" value={values.userDefinedField5} label={"Field 5"} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "userDefinedField5"))} helperText={getIn(errors, "userDefinedField5")} />
                              </Grid>

                              {/* Audit */}
                              <Grid item xs={12}>
                                <Box p={1}></Box>
                              </Grid>

                              <Grid item xs={12}>
                                <Box className={globalStyles.topic}>Audit</Box>
                              </Grid>

                              <Grid item xs={12} sm={6} md={3} lg={4}>
                                <TextField disabled={true} label="Last Modified" value={values.lastModifiedBy} />
                              </Grid>

                              <Grid item xs={12} sm={6} md={3} lg={4}>
                                <TextField disabled={true} label="Last Modified Date" value={formatDateTimeForDataGrid(values.lastModifiedDateTime)} />
                              </Grid>
                            </>
                          )}

                          {logs.length > 0 ? (
                            <>
                              <Grid item xs={12}>
                                <Box p={1}></Box>
                              </Grid>

                              <Grid item xs={12}>
                                <Box className={globalStyles.topic}>
                                  <Grid container>
                                    <Grid item xs={8}>
                                      Log
                                    </Grid>
                                    <Grid item xs={4}>
                                      <Box display="flex" justifyContent="flex-end" width={1}>
                                        {showOnlyLogCurrentErrors ? (
                                          <Tooltip arrow title="Click to see all log details">
                                            <IconButton
                                              onClick={() => {
                                                setShowOnlyLogCurrentErrors(false);
                                              }}
                                            >
                                              <LogErrorIcon color="error" />
                                            </IconButton>
                                          </Tooltip>
                                        ) : (
                                          <Tooltip arrow title="Click to see current errors">
                                            <IconButton
                                              onClick={() => {
                                                setShowOnlyLogCurrentErrors(true);
                                              }}
                                            >
                                              <ShowAllIcon color="secondary" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Grid>

                              <TableContainer style={{ maxHeight: "300px" }}>
                                <Table>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Note</TableCell>
                                      <TableCell align="right" className={pageStyles.logTimeStamp}>
                                        Timestamp
                                      </TableCell>
                                      <TableCell align="right" className={pageStyles.logRunId}>
                                        Run Id
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {showOnlyLogCurrentErrors ? (
                                      <>
                                        {logs
                                          .filter((item) => item.isError && item.latest)
                                          ?.map((pendingEnrollmentBatchLoadItemLog, index) => (
                                            <TableRow key={index}>
                                              <TableCell component="th" scope="row">
                                                {pendingEnrollmentBatchLoadItemLog.title}
                                                <br />
                                                {pendingEnrollmentBatchLoadItemLog.note}
                                              </TableCell>
                                              <TableCell align="right" className={pageStyles.logTimeStamp}>
                                                {formatDateTimeForDataGrid(pendingEnrollmentBatchLoadItemLog.timeStamp)}
                                              </TableCell>
                                              <TableCell align="right" className={pageStyles.logRunId}>
                                                {pendingEnrollmentBatchLoadItemLog.runLogId}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                      </>
                                    ) : (
                                      <>
                                        {logs.map((pendingEnrollmentBatchLoadItemLog, index) => (
                                          <TableRow key={index}>
                                            <TableCell component="th" scope="row">
                                              {pendingEnrollmentBatchLoadItemLog.title}
                                              <br />
                                              {pendingEnrollmentBatchLoadItemLog.note}
                                            </TableCell>
                                            <TableCell align="right" className={pageStyles.logTimeStamp}>
                                              {formatDateTimeForDataGrid(pendingEnrollmentBatchLoadItemLog.timeStamp)}
                                            </TableCell>
                                            <TableCell align="right" className={pageStyles.logRunId}>
                                              {pendingEnrollmentBatchLoadItemLog.runLogId}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </>
                                    )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </>
                          ) : null}
                        </Grid>

                        <Box p={3}></Box>
                      </Form>
                    </DialogContentText>
                  </DialogContent>

                  <DialogActions>
                    <Tooltip title="Report any issue" style={{ display: "none" }}>
                      <IconButton>
                        <ContactSupportIcon />
                      </IconButton>
                    </Tooltip>
                    {!readonly && advancedFlow ? (
                      <>
                        <Button onClick={() => saveOnHold()} color="secondary" className={pageStyles.onHoldButton} variant="contained">
                          <Hidden only={['xs']}>Put On&nbsp;</Hidden>
                          Hold
                        </Button>

                        <Button onClick={() => saveReProcess()} color="secondary" variant="contained" className={globalStyles.buttonDefault}>
                          ReProcess
                        </Button>
                      </>
                    ) : null}

                    {canWritePendingEnrollment(userFunctionAccess) && onHold ? (
                      <Button onClick={() => saveOnHold()} className={pageStyles.activateButton} variant="contained">
                        Activate
                      </Button>
                    ) : null}

                    {canWritePendingEnrollment(userFunctionAccess) ? (
                      <Tooltip title="Delete this account.">
                        <Button onClick={() => showDeleteConfirmationDialog()} className={globalStyles.errorText}>
                          <DeleteIcon />
                        </Button>
                      </Tooltip>
                    ) : null}

                    {!readonly && !advancedFlow ? (
                      <>
                        <Button onClick={() => saveOnHold()} color="secondary" className={pageStyles.onHoldButton} variant="contained">
                          <Hidden only={["xs"]}>Put On&nbsp;</Hidden>
                          Hold
                        </Button>

                        {!sidewalkValidationInProgress &&
                          (values.sidewalkValidationPassed === true ||
                            values.overrideSidewalkValidation === true ||
                            values.sidewalkValidationRequired === false) && (
                            <Button
                              disabled={!isFormEdited || !saveButtonVisible || !accountAddressIsValidated}
                              onClick={() => {
                                if (!scrollToFieldError(formReference)) {
                                  formReference.current.handleSubmit();
                                }
                              }}
                              color="secondary"
                              variant="contained"
                              className={globalStyles.buttonDefault}
                            >
                              Update
                            </Button>
                          )}
                      </>
                    ) : null}

                    <Button onClick={() => cancel()} autoFocus className={globalStyles.buttonDefault}>
                      <u>Cancel</u>
                    </Button>
                  </DialogActions>
                </>
              )}
            </Formik>
          </>
        )}
      </Dialog>
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    deleteButton: {
      backgroundColor: theme.palette.error.dark,
      color: "#ffffff",
      marginLeft: "4px",
      marginRight: "4px",

      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
      },
    },

    onHoldButton: {
      backgroundColor: theme.palette.info.dark,
      color: "#ffffff",
      marginLeft: "4px",
      marginRight: "4px",

      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
      },
    },
    activateButton: {
      backgroundColor: theme.palette.success.main,
      color: "#ffffff",
      marginLeft: "4px",
      marginRight: "4px",

      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
      },
    },
    logRunId: {
      width: "100px",
    },
    logTimeStamp: {
      width: "100px",
    },
  })
);

export default BatchItemReview;